import React, { useState, useEffect, useRef } from "react";
import { useHistory, Link } from "react-router-dom";
import { getAccessToken } from "frontend/utils/authenticateHelper.js";
import "./GamesSidebar.scss";

const GamesSidebar = ({
  games,
  challenges,
  currentGameId,
  onCreateGame,
  onHideGame,
  onClearCompleted,
  onSendChallenge,
  onUpdateChallenge,
  isAuthenticated,
  isGuest,
  onSignupClick,
  isOpen,
  onGameSelect,
  createGameError
}) => {
  const history = useHistory();
  const [showNewGameMenu, setShowNewGameMenu] = useState(false);
  const [challengeQuery, setChallengeQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchDone, setSearchDone] = useState(false);
  const searchTimeoutRef = useRef(null);
  const newGameContainerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (newGameContainerRef.current && !newGameContainerRef.current.contains(e.target)) {
        setShowNewGameMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const activeGames = games.filter(game => !game.complete);
  const yourMoveGames = activeGames.filter(game => game.your_turn);
  const waitingGames = activeGames.filter(game => !game.your_turn);
  const completedGames = games.filter(game => game.complete);

  const incomingChallenges = (challenges || []).filter(c => !c.you_are_challenger && c.status === 'pending');
  const sentChallenges = (challenges || []).filter(c => c.you_are_challenger && c.status === 'pending');

  useEffect(() => {
    if (!showNewGameMenu) {
      setChallengeQuery("");
      setSearchResults([]);
      setSearchDone(false);
    }
  }, [showNewGameMenu]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!challengeQuery.trim()) {
      setSearchResults([]);
      setSearchDone(false);
      return;
    }
    // Guests are blocked from user search server-side; searching anyway would
    // render a misleading "No players found".
    if (isGuest) {
      setSearchResults([]);
      setSearchDone(true);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v1/users/search?query=${encodeURIComponent(challengeQuery)}`, {
          headers: {
            AUTHORIZATION: `Bearer ${getAccessToken()}`,
            Accept: "application/json"
          }
        });
        const data = await res.json();
        setSearchResults(data.users || []);
      } catch (_) {
        setSearchResults([]);
      } finally {
        setSearchDone(true);
      }
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [challengeQuery, isGuest]);

  const handleSendChallenge = (userId) => {
    onSendChallenge(userId);
    setChallengeQuery("");
    setSearchResults([]);
    setShowNewGameMenu(false);
  };

  const handleGameClick = (gameId) => {
    history.push(`/games/${gameId}`);
    if (onGameSelect) onGameSelect();
  };

  const handleCreateGame = (aiDifficulty = null) => {
    setShowNewGameMenu(false);
    onCreateGame(aiDifficulty);
  };

  const handleHideGame = (e, gameId) => {
    e.stopPropagation();
    onHideGame(gameId);
  };

  return (
    <div className={`games-sidebar ${isOpen ? 'open' : ''}`}>
      <Link to="/games/how-to-play" className="how-to-play-link" onClick={onGameSelect}>
        <span className="link-icon">?</span>
        How to Play
      </Link>

      <div className="new-game-container" ref={newGameContainerRef}>
        <button
          className="create-game-btn"
          onClick={() => setShowNewGameMenu(!showNewGameMenu)}
        >
          <span className="btn-icon">+</span>
          New Game
        </button>
        {showNewGameMenu && (
          <div className="new-game-menu">
            <button onClick={() => handleCreateGame(null)}>
              <span className="menu-icon">&#x1F3AE;</span>
              Find a match
            </button>
            <button onClick={() => handleCreateGame('easy')}>
              <span className="menu-icon">&#x1F916;</span>
              vs AI (Easy)
            </button>
            <button onClick={() => handleCreateGame('hard')}>
              <span className="menu-icon">&#x1F916;</span>
              vs AI (Hard)
            </button>
            <div className="challenge-in-menu">
              <div className="challenge-search-row">
                <span className="menu-icon">&#x1F465;</span>
                <input
                  className="challenge-search-input"
                  type="text"
                  placeholder="Play a friend..."
                  value={challengeQuery}
                  onChange={e => setChallengeQuery(e.target.value)}
                  onClick={e => e.stopPropagation()}
                />
              </div>
              {challengeQuery.trim() && (searchDone || searchResults.length > 0) && (
                <div className="challenge-search-results">
                  {isGuest && (
                    <button className="search-hint search-hint-btn" onClick={onSignupClick}>
                      Sign up to play a friend
                    </button>
                  )}
                  {!isGuest && searchDone && searchResults.length === 0 && (
                    <div className="search-hint">No players found</div>
                  )}
                  {searchResults.map(u => (
                    <button
                      key={u.id}
                      className="search-result-item"
                      onClick={() => handleSendChallenge(u.id)}
                    >
                      <div className="game-avatar">{u.username.charAt(0).toUpperCase()}</div>
                      <span>{u.username}</span>
                      <span className="challenge-send-label">Play</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {createGameError && (
        <p className="create-game-error">{createGameError}</p>
      )}

      {incomingChallenges.length > 0 && (
        <div className="sidebar-section">
          <div className="section-header">
            <span className="header-icon">⚔️</span>
            <h3>Challenges</h3>
          </div>
          <div className="game-list">
            {incomingChallenges.map(challenge => (
              <div key={challenge.id} className="challenge-banner incoming">
                <div className="challenge-banner-body">
                  <div className="game-avatar">
                    {challenge.challenger.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="challenge-banner-text">
                    <strong>{challenge.challenger.username}</strong> challenged you
                  </span>
                </div>
                <div className="challenge-banner-actions">
                  <button
                    className="challenge-accept-btn"
                    onClick={() => onUpdateChallenge(challenge.id, 'accepted')}
                  >
                    Accept
                  </button>
                  <button
                    className="challenge-dismiss-btn"
                    onClick={() => onUpdateChallenge(challenge.id, 'declined')}
                    title="Decline"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sentChallenges.length > 0 && (
        <div className="sidebar-section">
          <div className="section-header">
            <span className="header-icon">📤</span>
            <h3>Sent Challenges</h3>
          </div>
          <div className="game-list">
            {sentChallenges.map(challenge => (
              <div key={challenge.id} className="challenge-banner sent">
                <div className="challenge-banner-body">
                  <div className="game-avatar">
                    {challenge.challenged.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="challenge-banner-text">
                    <strong>{challenge.challenged.username}</strong>
                    <span className="challenge-pending-label">Pending</span>
                  </span>
                </div>
                <button
                  className="challenge-dismiss-btn"
                  onClick={() => onUpdateChallenge(challenge.id, 'cancelled')}
                  title="Cancel"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sidebar-section">
        <div className="section-header">
          <span className="header-icon">⚡</span>
          <h3>Your Move</h3>
        </div>
        <div className="game-list">
          {yourMoveGames.length > 0 ? (
            yourMoveGames.map(game => (
              <div
                key={game.id}
                className={`game-item ${currentGameId == game.id ? 'active' : ''}`}
                onClick={() => handleGameClick(game.id)}
              >
                <div className="game-avatar">
                  {game.them ? game.them.username.charAt(0).toUpperCase() : "?"}
                </div>
                <div className="game-info">
                  <div className="game-opponent">
                    {game.them ? game.them.username : "Waiting..."}
                  </div>
                  <div className="game-score">
                    {game.your_score} - {game.their_score}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              No games waiting for your move
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-section">
        <div className="section-header">
          <span className="header-icon">⏳</span>
          <h3>Their Turn</h3>
        </div>
        <div className="game-list">
          {waitingGames.length > 0 ? (
            waitingGames.map(game => (
              <div
                key={game.id}
                className={`game-item ${currentGameId == game.id ? 'active' : ''}`}
                onClick={() => handleGameClick(game.id)}
              >
                <div className="game-avatar">
                  {game.them ? game.them.username.charAt(0).toUpperCase() : "?"}
                </div>
                <div className="game-info">
                  <div className="game-opponent">
                    {game.them ? game.them.username : "Waiting..."}
                  </div>
                  <div className="game-score">
                    {game.your_score} - {game.their_score}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              No games waiting for opponent
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-section">
        <div className="section-header">
          <span className="header-icon">✓</span>
          <h3>Completed</h3>
          {completedGames.length > 0 && (
            <button className="clear-completed-btn" onClick={onClearCompleted}>
              Clear
            </button>
          )}
        </div>
        <div className="game-list">
          {completedGames.length > 0 ? (
            completedGames.map(game => (
              <div
                key={game.id}
                className={`game-item completed ${game.your_result} ${currentGameId == game.id ? 'active' : ''}`}
                onClick={() => handleGameClick(game.id)}
              >
                <div className="game-avatar">
                  {game.them ? game.them.username.charAt(0).toUpperCase() : "?"}
                </div>
                <div className="game-info">
                  <div className="game-opponent">
                    {game.them ? game.them.username : "Unknown"}
                  </div>
                  <div className="game-score">
                    {game.your_score} - {game.their_score}
                  </div>
                </div>
                <div className="game-result-container">
                  <div className={`game-result ${game.your_result}`}>
                    {game.your_result === 'win' ? 'W' : game.your_result === 'tie' ? 'T' : 'L'}
                  </div>
                  <button
                    className="hide-game-btn"
                    onClick={(e) => handleHideGame(e, game.id)}
                    title="Hide game"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              No completed games
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-social-links">
        <a
          href="https://www.facebook.com/Kingof20"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="King of 20 on Facebook"
          className="social-link"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
          </svg>
        </a>
        <a
          href="https://x.com/Kof20Game"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="King of 20 on X"
          className="social-link"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932zm-1.292 19.494h2.039L6.486 3.24H4.298z" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default GamesSidebar;
